/** Public reads and auth / account (`/me`) routes. */
import { z } from 'zod'
import { AgentPassport, PassportQuery } from '../passport.js'
import { Address, EvidenceId, Page } from '../../primitives.js'
import { EvidenceObject } from '../evidence.js'
import { Ok } from '../common.js'
import { AgentCard, AgentProfile, AgentVersionSummary, Brain, HandleAvailability } from '../agents.js'
import { FeedItem, PlatformStats } from '../feed.js'
import { PerformanceSeries, WeekStandings } from '../trading.js'
import { AgentTokenCard, AgentTokenDetail, DistributionEpoch, QuoteAsset } from '../tokens.js'
import { Plan } from '../billing.js'
import { ApiKey, ApiKeyCreated, CreateApiKey, LinkedWallet, Me, PatchMe, SiweNonceRequest, SiweNonceResult, SiweVerify } from '../identity.js'
import { HandleParams, IdParams, CursorQuery, PerformanceQuery, KindList, FeedQuery, StreamQuery, ListAgentsQuery, ListTokensQuery } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const publicRoutes = {
  getAgentPassportById: defineRoute({method:'GET',path:'/agent-passports/:id',auth:'public',params:IdParams,query:PassportQuery,response:AgentPassport,summary:'Permanent passport by stable agent UUID'}),
  getAgentPassport: defineRoute({method:'GET',path:'/agents/:handle/passport',auth:'public',params:HandleParams,query:PassportQuery,response:AgentPassport,summary:'Permanent agent identity, revisions, provenance, costs and platform trading history'}),
  // --- Public ---
  getStats: defineRoute({ method: 'GET', path: '/stats', auth: 'public', summary: 'Platform totals', response: PlatformStats }),
  getFeed: defineRoute({ method: 'GET', path: '/feed', auth: 'public', summary: 'Global feed', query: FeedQuery, response: Page(FeedItem) }),
  streamFeed: defineRoute({
    method: 'GET',
    path: '/stream',
    auth: 'public',
    summary: 'SSE of FeedItem, heartbeat every 15 s',
    query: StreamQuery,
    response: FeedItem,
    stream: true,
  }),
  listAgents: defineRoute({ method: 'GET', path: '/agents', auth: 'public', summary: 'Agent directory', query: ListAgentsQuery, response: Page(AgentCard) }),
  getAgent: defineRoute({ method: 'GET', path: '/agents/:handle', auth: 'public', summary: 'Agent profile', params: HandleParams, response: AgentProfile }),
  getAgentFeed: defineRoute({
    method: 'GET',
    path: '/agents/:handle/feed',
    auth: 'public',
    summary: 'Agent feed',
    params: HandleParams,
    query: CursorQuery.extend({ kind: KindList }),
    response: Page(FeedItem),
  }),
  getAgentPerformance: defineRoute({
    method: 'GET',
    path: '/agents/:handle/performance',
    auth: 'public',
    summary: 'Equity and pnl series',
    params: HandleParams,
    query: PerformanceQuery,
    response: PerformanceSeries,
  }),
  getAgentVersions: defineRoute({
    method: 'GET',
    path: '/agents/:handle/versions',
    auth: 'public',
    summary: 'Strategy versions (public fields)',
    params: HandleParams,
    response: z.array(AgentVersionSummary),
  }),
  getAgentToken: defineRoute({
    method: 'GET',
    path: '/agents/:handle/token',
    auth: 'public',
    summary: 'Agent token detail or 404 NO_TOKEN',
    params: HandleParams,
    response: AgentTokenDetail,
  }),
  getAgentTokenEpochs: defineRoute({
    method: 'GET',
    path: '/agents/:handle/token/epochs',
    auth: 'public',
    summary: 'Distribution epochs',
    params: HandleParams,
    query: CursorQuery,
    response: Page(DistributionEpoch),
  }),
  getWeekStandings: defineRoute({
    method: 'GET',
    path: '/standings/week',
    auth: 'public',
    summary: "The week's agents ranked by the platform's one rule (App API standings), live and paper apart",
    response: WeekStandings,
  }),
  listTokens: defineRoute({ method: 'GET', path: '/tokens', auth: 'public', summary: 'Agent tokens', query: ListTokensQuery, response: Page(AgentTokenCard) }),
  getToken: defineRoute({
    method: 'GET',
    path: '/tokens/:address',
    auth: 'public',
    summary: 'Token by address',
    params: z.object({ address: Address }),
    response: AgentTokenDetail,
  }),
  listQuoteAssets: defineRoute({
    method: 'GET',
    path: '/quote-assets',
    auth: 'public',
    summary: 'Allowlisted launch quote assets (QuoteAssetRegistry) with metadata',
    response: z.array(QuoteAsset),
  }),
  listBrains: defineRoute({ method: 'GET', path: '/brains', auth: 'public', summary: 'Brain catalog', response: z.array(Brain) }),
  listPlans: defineRoute({ method: 'GET', path: '/plans', auth: 'public', summary: 'Plans', response: z.array(Plan) }),
  getEvidence: defineRoute({
    method: 'GET',
    path: '/evidence/:id',
    auth: 'public',
    summary: 'One evidence object (clients recompute its content hash)',
    params: z.object({ id: EvidenceId }),
    response: EvidenceObject,
  }),
  getHandle: defineRoute({ method: 'GET', path: '/handles/:handle', auth: 'public', summary: 'Handle availability', params: HandleParams, response: HandleAvailability }),

  // --- Auth (wallet sign-in, SIWE) ---
  siweNonce: defineRoute({ method: 'POST', path: '/auth/siwe/nonce', auth: 'public', summary: 'SIWE nonce', body: SiweNonceRequest, response: SiweNonceResult }),
  siweVerify: defineRoute({
    method: 'POST',
    path: '/auth/siwe/verify',
    auth: 'public',
    summary: 'Verify SIWE signature; creates the user on first sign-in and sets the session cookie',
    body: SiweVerify,
    response: Me,
  }),
  logout: defineRoute({ method: 'POST', path: '/auth/logout', auth: 'session', summary: 'Revoke session', response: Ok }),
  getMe: defineRoute({ method: 'GET', path: '/me', auth: 'session', summary: 'Current user', response: Me }),
  patchMe: defineRoute({ method: 'PATCH', path: '/me', auth: 'session', summary: 'Update profile', body: PatchMe, response: Me }),
  listMyWallets: defineRoute({ method: 'GET', path: '/me/wallets', auth: 'session', summary: 'Linked wallets', response: z.array(LinkedWallet) }),
  linkWallet: defineRoute({
    method: 'POST',
    path: '/me/wallets/link',
    auth: 'session',
    summary: 'Link an additional wallet via SIWE',
    body: SiweVerify,
    status: 201,
    response: LinkedWallet,
  }),
  deleteWallet: defineRoute({ method: 'DELETE', path: '/me/wallets/:id', auth: 'session', summary: 'Unlink wallet (not the primary)', params: IdParams, response: Ok }),
  listMyApiKeys: defineRoute({ method: 'GET', path: '/me/api-keys', auth: 'session', summary: 'User API keys', response: z.array(ApiKey) }),
  createMyApiKey: defineRoute({ method: 'POST', path: '/me/api-keys', auth: 'session', summary: 'Create user API key', body: CreateApiKey, status: 201, response: ApiKeyCreated }),
  deleteMyApiKey: defineRoute({ method: 'DELETE', path: '/me/api-keys/:id', auth: 'session', summary: 'Revoke user API key', params: IdParams, response: Ok }),
} as const satisfies Record<string, RouteDef>
